import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { TokenStorage } from '../utils/cookieStorage';



interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

interface WebSocketContextType {
  isConnected: boolean;
  send: (data: WebSocketMessage) => void;
  subscribe: (callback: (data: WebSocketMessage) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
  wsUrl?: string;
}

/**
 * Global WebSocket Provider - Manages a single WebSocket connection
 * Place this at the app root (e.g., in your main App component)
 * This ensures only one WebSocket connection across the entire app
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  enabled = true,
  wsUrl = window.ENV_CONFIG?.WEBSOCKET_URL || 'ws://localhost:4000/api/study-updates',
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = React.useRef<WebSocket | null>(null);
  const subscribersRef = React.useRef<Set<(data: WebSocketMessage) => void>>(new Set());
  const reconnectAttemptsRef = React.useRef(0);
  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const maxReconnectAttempts = 5;

  const getAuthToken = useCallback(() => {
    try {
      const token = TokenStorage.getAccessToken();
      if (token) {
        console.log('[WebSocket Provider] Token retrieved from TokenStorage');
        return token;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    // Clear existing heartbeat interval if any
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    // Send heartbeat every 45 seconds
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const heartbeatMessage: WebSocketMessage = {
          type: 'heartbeat',
          timestamp: new Date().toISOString(),
        };
        wsRef.current.send(JSON.stringify(heartbeatMessage));
        console.log('[WebSocket] Heartbeat sent');
      }
    }, 45000); // 45 seconds
  }, []);

  const connectWebSocket = useCallback(() => {
    // Don't try to connect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const token = getAuthToken();

    // Don't attempt without valid token
    if (!token) {
      console.warn('[WebSocket Provider] No token available, skipping connection');
      return;
    }

    try {
      const url = `${wsUrl}?token=${token}`;
      console.log('[WebSocket Provider] Connecting to:', url);

      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('[WebSocket Provider] Connected');
        wsRef.current = ws;
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        startHeartbeat();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WebSocket Provider] Message received:', data.type);

          // Notify subscribers
          subscribersRef.current.forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('[WebSocket] Subscriber error:', error);
            }
          });
        } catch (error) {
          console.error('[WebSocket] Parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[WebSocket Provider] Error:', error);
      };

      ws.onclose = () => {
        console.log('[WebSocket Provider] Disconnected');
        wsRef.current = null;
        setIsConnected(false);

        // Clear heartbeat interval
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Reconnect if enabled and under max attempts
        if (enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);
          console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };
    } catch (error) {
      console.error('[WebSocket Provider] Connection error:', error);
    }
  }, [enabled, getAuthToken, wsUrl, startHeartbeat]);
  // Connect on mount and when enabled changes
  useEffect(() => {
    if (!enabled) {
      // Cleanup when disabled
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    // Try to connect
    connectWebSocket();

    // Cleanup function
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [enabled, connectWebSocket]);

  const send = useCallback(
    (data: WebSocketMessage) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        console.log('[WebSocket Provider] Sending message:', data);
        wsRef.current.send(JSON.stringify(data));
      } else {
        console.warn('[WebSocket] Not connected');
      }
    },
    []
  );

  const subscribe = useCallback(
    (callback: (data: WebSocketMessage) => void) => {
      subscribersRef.current.add(callback);

      // Return unsubscribe function
      return () => {
        subscribersRef.current.delete(callback);
      };
    },
    []
  );

  const value: WebSocketContextType = {
    isConnected,
    send,
    subscribe,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

/**
 * Hook to use WebSocket context in any component
 * Returns context if within WebSocketProvider, otherwise returns a no-op implementation
 * This allows graceful degradation if provider is not available
 */
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);

  if (!context) {
    // Return a no-op implementation if context is not available
    console.warn('[useWebSocketContext] Not within WebSocketProvider - WebSocket features disabled');
    return {
      isConnected: false,
      send: (data) => console.warn('[WebSocket] No-op send:', data),
      subscribe: (callback) => {
        console.warn('[WebSocket] No-op subscribe');
        return () => {}; // Return no-op unsubscribe
      },
    };
  }

  return context;
};
